import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PhoneInput from './PhoneInput';
import AddressModal from './AddressModal';
import { validatePhoneNumber } from '../utils/phoneValidation';
import './LoginScreen.css';

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phone, setPhone] = useState('');
  const [loginWithPhone, setLoginWithPhone] = useState(false);
  const { login, register, completeRegistration, isLoading, showAddressModal, pendingUserData, pendingToken, setShowAddressModal } = useAuth();
  const navigate = useNavigate();

  // Debug logging for modal state

  // Debug logging for state changes
  useEffect(() => {
  }, [showAddressModal]);

  useEffect(() => {
  }, [isRegisterMode]);

  const handleAddressModalClose = () => {
    // Complete registration even if address modal is closed without saving
    console.log('🔍 LoginScreen: handleAddressModalClose - pendingUserData:', pendingUserData);
    if (pendingUserData && pendingToken) {
      completeRegistration(pendingUserData, pendingToken);
    }
    // Navigate to home page which will show onboarding
    navigate('/home', { replace: true });
  };

  const handleAddressModalSaved = () => {
    // Complete registration after saving address
    console.log('🔍 LoginScreen: handleAddressModalSaved - pendingUserData:', pendingUserData);
    if (pendingUserData && pendingToken) {
      completeRegistration(pendingUserData, pendingToken);
    }
    // Navigate to home page which will show onboarding
    navigate('/home', { replace: true });
  };

  // Clear all form fields when switching between login and register modes
  // BUT don't clear if we're showing the address modal (pending registration)
  useEffect(() => {
    if (!showAddressModal) { // Only clear fields if we're not showing the address modal
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setConfirmPassword('');
      setPhone('');
      setLoginWithPhone(false);
    }
  }, [isRegisterMode, showAddressModal]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const loginIdentifier = loginWithPhone ? phone : email;
    
    if (!loginIdentifier.trim() || !password.trim()) {
      alert('Please fill in all fields');
      return;
    }

    if (isRegisterMode) {
      if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
        alert('Please fill in all fields');
        return;
      }
      
      // Validate phone number
      const phoneValidation = validatePhoneNumber(phone);
      if (!phoneValidation.isValid) {
        alert(`Invalid phone number: ${phoneValidation.error}`);
        return;
      }
      
      if (password !== confirmPassword) {
        alert('Passwords do not match');
        return;
      }
      
      const result = await register({ username: `${firstName} ${lastName}`, email, phone, password });
      
      if (result.success && result.user && result.token) {
        
        // Force a small delay to check state
        setTimeout(() => {
        }, 100);
      } else {
        console.error('❌ Registration failed:', result.error);
        alert(`Registration Failed: ${result.error}`);
      }
    } else {
      // For login, validate phone number if using phone login
      if (loginWithPhone) {
        const phoneValidation = validatePhoneNumber(phone);
        if (!phoneValidation.isValid) {
          alert(`Invalid phone number: ${phoneValidation.error}`);
          return;
        }
      }
      
      const result = await login({ email: loginIdentifier, password });
      if (result.success) {
        navigate('/home', { replace: true });
      } else {
        alert(`Login Failed: ${result.error}`);
      }
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="logo-container">
          <img 
            src="/cluubz-logo-purple.png" 
            alt="Cluubz" 
            className="app-logo-extra-large"
          />
        </div>

        <form onSubmit={handleSubmit} className="form">
          <h2>{isRegisterMode ? 'Register' : 'Login'}</h2>
          
          {isRegisterMode && (
            <>
              <div className="name-fields">
                <input
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="input name-input"
                  autoComplete="given-name"
                />
                <input
                  type="text"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="input name-input"
                  autoComplete="family-name"
                />
              </div>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                autoComplete="email"
              />
              <PhoneInput
                value={phone}
                onChange={setPhone}
                placeholder="Phone"
                className="input"
                autoComplete="tel"
              />
            </>
          )}

          {!isRegisterMode && (
            <div className="login-type-toggle">
              <button
                type="button"
                className={`toggle-button ${!loginWithPhone ? 'active' : ''}`}
                onClick={() => setLoginWithPhone(false)}
              >
                📧 Email
              </button>
              <button
                type="button"
                className={`toggle-button ${loginWithPhone ? 'active' : ''}`}
                onClick={() => setLoginWithPhone(true)}
              >
                📱 Phone
              </button>
            </div>
          )}

          {!isRegisterMode && (
            <>
              {loginWithPhone ? (
                <PhoneInput
                  value={phone}
                  onChange={setPhone}
                  placeholder="Phone Number"
                  className="input"
                  autoComplete="tel"
                />
              ) : (
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input"
                  autoComplete="username"
                />
              )}
            </>
          )}
          
          <div className="password-field">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input password-input"
              autoComplete={isRegisterMode ? "new-password" : "current-password"}
            />
            <button
              type="button"
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? '🙈' : '👁️'}
            </button>
          </div>
          
          {isRegisterMode && (
            <div className="password-field">
              <input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="input password-input"
                autoComplete="new-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? '🙈' : '👁️'}
              </button>
            </div>
          )}

          <button 
            type="submit" 
            className={`button ${isLoading ? 'button-disabled' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : (isRegisterMode ? 'Register' : 'Login')}
          </button>

          <button 
            type="button"
            className="link-button"
            onClick={() => setIsRegisterMode(!isRegisterMode)}
          >
            {isRegisterMode ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
          </button>
        </form>
      </div>

      <AddressModal
        isOpen={showAddressModal}
        onClose={handleAddressModalClose}
        onSaved={handleAddressModalSaved}
        title="Please Provide Your Address"
        description="Please provide your address so we can match you to activities in your area."
        canSkip={true}
      />
    </div>
  );
};

export default LoginScreen;